import fp from 'lodash/fp';
import React, {
    ChangeEventHandler,
    useState,
    useCallback,
    useRef,
    useImperativeHandle,
    useMemo,
} from 'react';
import { formatDate, formatDateTime, formatTime, parseDate } from '@factorin/util';
import InputMask, { Props as InputMaskProps } from 'react-input-mask';

import { cn, ClassName } from 'Modules/ui/utils';


import { Calendar, CalendarProps } from '../Calendar';
import styles from './index.module.scss';
import { Popup } from '../Popup';


export type CalendarInputProps = ClassName & Pick<CalendarProps, 'disabledDays'> & Omit<InputMaskProps, 'value'> & {
    locale: 'en' | 'ru';

    onFocus?: () => void;
    onBlur?: () => void;

    disabled?: boolean;
    value: Date;
    onChange: (value: Date) => void;

    inputClassname?: string;
    children?: React.ReactElement[];

    withTime?: boolean;
};

export type CalendarInputMethods = {
    close(): void;
};

const getTimeFromDateString = (date: string) => {
    const time = date?.split(', ')[1];
    return {
        time: time || null,
        hours: Number(time?.split(':')[0]) || null,
        min: Number(time?.split(':')[1]) || null,
    };
};

export const CalendarInput = React.forwardRef<CalendarInputMethods, CalendarInputProps>(({
    className, locale,
    disabled, onFocus,
    onBlur, value, onChange,
    children, inputClassname,
    withTime, disabledDays,
    ...props
}, ref) => {
    const inputRef = useRef<HTMLInputElement>();
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        close: () => setIsOpen(false),
    }));

    const [inputValue, setInputValue] = useState<string>(null);
    const commitInputChange = useMemo(
        () => fp.throttle(200, (value: string) => {
            const newDate = parseDate(value, locale);
            if (withTime) {
                newDate?.setHours(
                    getTimeFromDateString(value).hours, getTimeFromDateString(value).min, 0, 0,
                );
            }
            onChange(newDate);
        }),
        [onChange, locale, withTime],
    );

    const formatFunc = useMemo(() => withTime ? formatDateTime : formatDate, [withTime]);

    const handleInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        e => {
            setInputValue(e.target.value);
            commitInputChange(e.target.value);
        },
        [commitInputChange],
    );

    const onInputBlur = useCallback(
        () => {
            setInputValue(null);
            if (onBlur && !isOpen)
                onBlur();
        },
        [onBlur, isOpen],
    );

    const onInputFocus = useCallback(
        () => {
            setIsOpen(true);
            if (onFocus)
                onFocus();
        },
        [onFocus],
    );

    const onCalendarChange = useCallback(
        (date: Date) => {
            onChange(date);
            setInputValue(null);
            if (!withTime) {
                setIsOpen(false);
                onBlur();
            }
        },
        [onChange, onBlur, withTime],
    );

    const onTimeChange = useCallback(
        e => {
            const [h, m] = e.target.value.split(':');
            const dateWithTime = value ? new Date(value) : new Date();
            dateWithTime.setHours(h, m, 0, 0);
            onChange(dateWithTime);
            setInputValue(null);
        },
        [onChange, value],
    );

    const onCalendarClose = useCallback(
        () => {
            onBlur();
            setIsOpen(false);
        },
        [onBlur],
    );

    return (
        <div className={cn(styles.calendarInput, className)}>
            <InputMask
                className={inputClassname}
                disabled={disabled}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                value={inputValue || formatFunc(value, locale)}
                onChange={handleInputChange}
                mask={`99.99.9999${withTime ? ', 99:99' : ''}`}
                {...props}
            >
                {inputProps => <input ref={inputRef} disabled={disabled} {...inputProps} />}
            </InputMask>
            <Popup
                isOpen={isOpen}
                closingDelay={50}
                onClose={onCalendarClose}
                overlayZIndex={40}
                parentPosition={
                    inputRef.current && inputRef.current.getBoundingClientRect()}
            >
                <div className={styles.calendar} data-testid='calendar-popup'>
                    <Calendar
                        locale={locale}
                        value={value}
                        onChange={onCalendarChange}
                        disabledDays={disabledDays}
                        {...props}
                    />
                    {withTime && (
                        <div className={styles.timeInputContainer}>
                            <input
                                data-testid='calendar-time'
                                type='time'
                                onChange={onTimeChange}
                                value={getTimeFromDateString(inputValue)?.time || formatTime(value)}
                                className={inputClassname}
                            />
                        </div>
                    )}
                    <div className={styles.children}>
                        {children}
                    </div>
                </div>
            </Popup>
        </div>
    );
});
